import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

function LookupDialog({
  open,
  title,
  searchLabel,
  placeholder,
  query,
  onQueryChange,
  results,
  onSelect,
  noResultsText,
  minQueryLength,
  onClose,
  searchType,
  searchContext,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={searchLabel}
          placeholder={placeholder}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          margin="normal"
          autoFocus
          data-testid="lookup-search-input"
          data-search-type={searchType}
          data-search-context={searchContext}
          data-timestamp={Date.now()}
          aria-label={`Search ${searchLabel.toLowerCase()}`}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 2, maxHeight: 400, overflow: "auto" }}>
          {query && query.length >= minQueryLength ? (
            Array.isArray(results) && results.length > 0 ? (
              <List>
                {results.map((result, index) => (
                  <ListItem key={`${result.code}-${index}`} disablePadding>
                    <ListItemButton 
                      onClick={() => onSelect(result)}
                      data-testid={`lookup-result-${index}`}
                      data-result-code={result.code}
                      aria-label="Select this result"
                    >
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle2" component="span" color="primary">
                              {result.code}
                            </Typography>
                            <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                              {result.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                {noResultsText}
              </Typography>
            )
          ) : (
            <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
              Type at least {minQueryLength} characters to search.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          data-testid="lookup-dialog-close-button"
          aria-label="Close lookup dialog"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

LookupDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  searchLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  noResultsText: PropTypes.string,
  minQueryLength: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  searchType: PropTypes.string,
  searchContext: PropTypes.string,
};

LookupDialog.defaultProps = {
  noResultsText: "No results found. Try a different search term.",
  minQueryLength: 2,
};

export default LookupDialog;
